import * as React from 'react';
import styled from 'styled-components';
import strings from '../../../../strings/strings.json';
import NavBackButton from 'components/buttons/NavBackButton';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { FlexColMd, FlexColSm, Headline2 } from 'components/Elements';
import EditActivityComponent from '../EditActivityComponent';
import Hint from 'components/Hint';
import ModulesList from './ModulesList';
import CreateTextImageTaskForm from '../activityForms/createActivityForms/CreateTextImageTaskForm';
import CreateVideoTaskForm from '../activityForms/createActivityForms/CreateVideoTaskForm';
import CreateAudioTaskForm from '../activityForms/createActivityForms/CreateAudioTaskForm';
import CreateQuestionnaireForm from '../activityForms/createActivityForms/CreateQuestionnaireForm';
import AddPresentationSlideForm from '../activityForms/presentationActivities/AddPresentationSlideForm';

export type ContentComponent =
    | { name: 'modulesList'; moduleId?: string }
    | { name: 'addTaskText'; moduleId: string }
    | { name: 'addTaskVideo'; moduleId: string }
    | { name: 'addTaskAudio'; moduleId: string }
    | { name: 'addQuestionnaire'; moduleId: string }
    | { name: 'addSlide'; moduleId: string }
    | { name: 'editActivity'; activityId: string };

export default function CourseStructureAndContentComponent(): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setActiveStep, editActivityId, setEditActivityId, editMode } = React.useContext(BuildCourseContext);

    const [component, setComponent] = React.useState<ContentComponent>({ name: 'modulesList' });
    const [activeModuleId, setActiveModuleId] = React.useState<string>();

    React.useEffect(() => {
        if (editActivityId) {
            const activities = course.modules.flatMap((module) => module.activities);
            const activity = activities.find((activity) => activity.id === editActivityId);
            if (activity) {
                setComponent({ name: 'editActivity', activityId: activity.id });
                setActiveModuleId(activity.moduleId);
            }
        } else if (course.modules.length === 1) {
            setActiveModuleId(course.modules[0].id);
        }

        return () => {
            setEditActivityId('');
        };
    }, [course]); //eslint-disable-line

    React.useEffect(() => {
        if (component.name === 'modulesList' && component.moduleId) {
            setActiveModuleId(component.moduleId);
        }
    }, [component]);

    React.useEffect(() => {
        if (activeModuleId) {
            const activities = course.modules.flatMap((module) => module.activities);
            const activity = activities.find((activity) => activity.id === editActivityId);
            if (activity) {
                setComponent({ name: 'editActivity', activityId: activity.id });
                setActiveModuleId(activity.moduleId);
            }
        } else if (course.modules.length === 1) {
            setActiveModuleId(course.modules[0].id);
        }
    }, [activeModuleId]); //eslint-disable-line

    switch (component.name) {
        case 'modulesList': {
            return (
                <ModulesListContainer>
                    <FlexColSm>
                        <Headline2>{strings.courseEditorPage.steps.structureAndContent.title}</Headline2>
                        {!editMode && <Hint hint={strings.courseEditorPage.steps.structureAndContent.hint} />}
                    </FlexColSm>
                    <ModulesList
                        activeModuleId={activeModuleId}
                        setComponent={setComponent}
                        goToGeneratedContent={() => setActiveStep('courseAIGeneratedContent')}
                    />
                </ModulesListContainer>
            );
        }
        case 'addTaskText':
            return (
                <Base setComponent={setComponent} moduleId={component.moduleId}>
                    <CreateTextImageTaskForm
                        returnToModulesList={() => setComponent({ name: 'modulesList', moduleId: component.moduleId })}
                        moduleId={component.moduleId}
                    />
                </Base>
            );
        case 'addTaskVideo':
            return (
                <Base setComponent={setComponent} moduleId={component.moduleId}>
                    <CreateVideoTaskForm
                        returnToModulesList={() => setComponent({ name: 'modulesList', moduleId: component.moduleId })}
                        moduleId={component.moduleId}
                    />
                </Base>
            );
        case 'addTaskAudio':
            return (
                <Base setComponent={setComponent} moduleId={component.moduleId}>
                    <CreateAudioTaskForm
                        returnToModulesList={() => setComponent({ name: 'modulesList', moduleId: component.moduleId })}
                        moduleId={component.moduleId}
                    />
                </Base>
            );
        case 'addQuestionnaire':
            return (
                <Base setComponent={setComponent} moduleId={component.moduleId}>
                    <CreateQuestionnaireForm
                        returnToModulesList={() => setComponent({ name: 'modulesList', moduleId: component.moduleId })}
                        moduleId={component.moduleId}
                    />
                </Base>
            );
        case 'addSlide':
            return (
                <Base setComponent={setComponent} moduleId={component.moduleId}>
                    <AddPresentationSlideForm
                        returnToModulesList={() => setComponent({ name: 'modulesList', moduleId: component.moduleId })}
                        moduleId={component.moduleId}
                    />
                </Base>
            );
        case 'editActivity':
            const activity = course.modules.flatMap((m) => m.activities).find((a) => a.id === component.activityId);
            if (!activity) return <></>;
            return (
                <Base setComponent={setComponent} moduleId={activity.moduleId}>
                    <EditActivityComponent
                        activity={activity}
                        returnToCourseStructure={() =>
                            setComponent({ name: 'modulesList', moduleId: activity.moduleId })
                        }
                    />
                </Base>
            );
    }
}

type BaseProps = {
    children: React.ReactNode;
    setComponent: (value: React.SetStateAction<ContentComponent>) => void;
    moduleId: string;
};

const Base: React.FC<BaseProps> = ({ children, setComponent, moduleId }) => {
    return (
        <>
            <NavBackButton
                backButtonTitle={strings.courseContentPage.backToCourseContentButton}
                backButtonAria={strings.courseContentPage.backToCourseContentButton}
                backButtonHref={''}
                buttonFunctionOverride={() => setComponent({ name: 'modulesList', moduleId: moduleId })}
            />
            {children}
        </>
    );
};

const ModulesListContainer = styled(FlexColMd)`
    max-width: 900px;
`;
